import React, { useMemo, useState, useEffect } from "react";
import "./ComplianceManagement.css";
import "./ClientManagement.css";
import CreateComplianceFolderModal from "./CreateComplianceFolderModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolderPlus,
  faFileAlt,
  faPalette,
  faCheck,
  faFolder,
  faEllipsisV,
  faTrashAlt,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "./Modal";
import {
  fetchComplianceFolders,
  createComplianceFolder,
  updateComplianceFolder,
  deleteComplianceFolder,
} from "../utils/api";
import PageHeader from "./layout/PageHeader";
import { ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Folder colors
const folderColors = [
  "#2196F3", // Blue
  "#4CAF50", // Green
  "#FF9800", // Orange
  "#f44336", // Red
  "#9C27B0", // Purple
  "#00BCD4", // Cyan
  "#E91E63", // Pink
  "#607D8B", // Gray
];

const ComplianceManagement = ({
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [complianceFolders, setComplianceFolders] = useState([]);
  const [isEditFolderModalOpen, setIsEditFolderModalOpen] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [editFolderDescription, setEditFolderDescription] = useState("");
  const [editFolderColor, setEditFolderColor] = useState("#2196F3");
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedFolderIds, setExpandedFolderIds] = useState(() => new Set());
  const [folderMenuOpenFor, setFolderMenuOpenFor] = useState(null);
  const [createParentFolderId, setCreateParentFolderId] = useState(null);

  useEffect(() => {
    const onDown = (e) => {
      if (e?.target?.closest?.(".cm-folder-menu")) return;
      setFolderMenuOpenFor(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Fetch compliance folders from API
  useEffect(() => {
    const loadFolders = async () => {
      if (!activeLocation) return;

      const locId = activeLocation.id || activeLocation._id;
      if (!locId) {
        console.error("Active location has no ID:", activeLocation);
        return;
      }

      setComplianceFolders([]);
      setIsLoading(true);
      try {
        console.log("[Compliance] Fetching folders for location:", locId);
        const folders = await fetchComplianceFolders({ locationId: locId });
        setComplianceFolders(folders);
      } catch (error) {
        console.error("[Compliance] Failed to fetch folders:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadFolders();
  }, [activeLocation]);

  const handleCreateFolderAPI = async (folderData) => {
    try {
      const payload = {
        ...folderData,
        locationId: activeLocation
          ? activeLocation.id || activeLocation._id
          : null,
        parentFolderId: createParentFolderId || null,
        parentId: createParentFolderId || null,
      };

      const newFolder = await createComplianceFolder(payload);
      setComplianceFolders((prev) => [...prev, newFolder]);
      setIsCreateFolderModalOpen(false);
      setCreateParentFolderId(null);
      console.log("[Compliance] Folder created:", newFolder._id);

      if (payload.parentFolderId) {
        setExpandedFolderIds((prev) => {
          const next = new Set(prev);
          next.add(payload.parentFolderId);
          return next;
        });
      }

      const newId = newFolder?.id || newFolder?._id;
      if (newId) navigate(`/compliance-management/${newId}`);
    } catch (error) {
      console.error("[Compliance] Failed to create folder:", error);
      alert("Failed to create compliance folder.");
    }
  };

  const handleEditFolder = (folderId) => {
    const folder = complianceFolders.find(
      (f) => f.id === folderId || f._id === folderId
    );

    if (folder) {
      setFolderToEdit(folder);
      setEditFolderName(folder.name);
      setEditFolderDescription(folder.description);
      setEditFolderColor(folder.color || "#2196F3");
      setIsEditFolderModalOpen(true);
    } else {
      console.error("[Compliance] Folder not found for edit:", folderId);
    }
  };

  const handleUpdateFolder = async () => {
    try {
      const updatedData = {
        name: editFolderName,
        description: editFolderDescription,
        color: editFolderColor,
      };
      const idToUpdate = folderToEdit.id || folderToEdit._id;

      const updatedFolder = await updateComplianceFolder(
        idToUpdate,
        updatedData
      );

      setComplianceFolders((prevFolders) =>
        prevFolders.map((folder) =>
          folder.id === idToUpdate || folder._id === idToUpdate
            ? updatedFolder
            : folder
        )
      );

      setIsEditFolderModalOpen(false);
      setFolderToEdit(null);
      setEditFolderName("");
      setEditFolderDescription("");
      console.log("[Compliance] Folder updated:", idToUpdate);
    } catch (error) {
      console.error("[Compliance] Failed to update folder:", error);
      alert("Failed to update folder.");
    }
  };

  const handleCloseEditFolderModal = () => {
    setIsEditFolderModalOpen(false);
    setFolderToEdit(null);
    setEditFolderName("");
    setEditFolderDescription("");
    setEditFolderColor("#2196F3");
  };

  const handleDeleteFolder = (folderId) => {
    const folder = complianceFolders.find(
      (f) => f.id === folderId || f._id === folderId
    );
    if (folder) {
      setFolderToDelete(folder);
      setIsDeleteConfirmModalOpen(true);
    } else {
      console.error("[Compliance] Folder not found for delete:", folderId);
    }
  };

  const confirmDeleteFolder = async () => {
    try {
      const idToDelete = folderToDelete.id || folderToDelete._id;
      await deleteComplianceFolder(idToDelete);
      setComplianceFolders((prev) =>
        prev.filter(
          (folder) => folder.id !== idToDelete && folder._id !== idToDelete
        )
      );
      setIsDeleteConfirmModalOpen(false);
      setFolderToDelete(null);
      console.log("[Compliance] Folder deleted:", idToDelete);
    } catch (error) {
      console.error("[Compliance] Failed to delete folder:", error);
      alert("Failed to delete folder.");
    }
  };

  const handleCloseDeleteConfirmModal = () => {
    setIsDeleteConfirmModalOpen(false);
    setFolderToDelete(null);
  };

  const foldersByParent = useMemo(() => {
    const map = new Map();
    (complianceFolders || []).forEach((f) => {
      const pid = f?.parentFolderId || f?.parentId || null;
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(f);
    });
    for (const [, arr] of map.entries()) {
      arr.sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      );
    }
    return map;
  }, [complianceFolders]);

  const getFolderChildren = (parentId) =>
    foldersByParent.get(parentId || null) || [];

  const rootFolders = useMemo(() => {
    const q = String(searchTerm || "").trim().toLowerCase();
    if (!q) return getFolderChildren(null);
    // Search roots only (same behavior as client management)
    return getFolderChildren(null).filter((folder) => {
      return (
        (folder?.name || "").toLowerCase().includes(q) ||
        (folder?.description || "").toLowerCase().includes(q)
      );
    });
  }, [foldersByParent, searchTerm]);

  const toggleExpanded = (id) => {
    if (!id) return;
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!activeLocation) {
    return (
      <div className="compliance-management-container page-shell">
        <PageHeader
          title="Compliance Management"
          subtitle="Securely organize compliance documents, licenses, and certifications."
          icon={<ShieldCheck className="h-6 w-6 text-white" />}
          activeLocation={activeLocation}
          locations={locations}
          onChangeLocation={onChangeLocation}
          companyDisplayName={companyDisplayName}
          currentUser={currentUser}
          onLogout={onLogout}
          onLock={onLock}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <FontAwesomeIcon
            icon={faFileAlt}
            style={{ fontSize: "48px", color: "#667eea" }}
          />
          <div style={{ fontSize: "18px", color: "#666" }}>
            Loading location...
          </div>
          <div style={{ fontSize: "14px", color: "#999" }}>
            Please select a location from the dropdown above
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compliance-management-container page-shell">
      <PageHeader
        title="Compliance Management"
        subtitle="Securely organize compliance documents, licenses, and certifications."
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
        activeLocation={activeLocation}
        locations={locations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={currentUser}
        onLogout={onLogout}
        onLock={onLock}
      />

      <div
        className={`cm-shell cm-shell--compliance${folderMenuOpenFor ? " cm-shell--menu-open" : ""}`}
      >
        <aside className="cm-sidebar">
          <div className="cm-sidebar-header">
            <div className="cm-sidebar-title">Folders</div>
          </div>

          <div className="cm-folder-list">
            {isLoading ? (
              <div className="cm-empty">Loading folders...</div>
            ) : rootFolders.length === 0 ? (
              <div className="cm-empty">No folders yet.</div>
            ) : (
              (() => {
                const renderNode = (f, level = 0) => {
                  const id = f?.id || f?._id;
                  const displayName = f?.name || "Unnamed Folder";
                  const count = Number(f?.documentCount) || 0;
                  const children = getFolderChildren(id);
                  const hasChildren = children.length > 0;
                  const isExpanded = expandedFolderIds.has(id);

                  return (
                    <div
                      key={id || `${displayName}-${level}`}
                      className="cm-folder-node"
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        className="cm-folder-item"
                        style={{ paddingLeft: 10 + level * 16 }}
                        onClick={() => {
                          if (!id) return;
                          navigate(`/compliance-management/${id}`);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (!id) return;
                            navigate(`/compliance-management/${id}`);
                          }
                        }}
                      >
                        <span
                          className="cm-folder-icon"
                          style={
                            f?.color && String(f.color).startsWith("#")
                              ? { backgroundColor: `${f.color}22`, color: f.color }
                              : {}
                          }
                        >
                          <FontAwesomeIcon icon={faFolder} />
                        </span>
                        <span className="cm-folder-text">
                          <span className="cm-folder-name" data-fullname={displayName}>
                            {displayName}
                          </span>
                        </span>
                        <span className="cm-folder-count">{count}</span>
                        <span className="cm-folder-menu">
                          <button
                            type="button"
                            className="cm-folder-kebab"
                            aria-label="Folder actions"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setFolderMenuOpenFor((prev) =>
                                prev === id ? null : id,
                              );
                            }}
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </button>
                          {folderMenuOpenFor === id && (
                            <div className="cm-menu cm-menu-folder">
                              <button
                                type="button"
                                className="cm-menu-item"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setFolderMenuOpenFor(null);
                                  setCreateParentFolderId(id);
                                  setIsCreateFolderModalOpen(true);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faFolderPlus}
                                  style={{ marginRight: 10 }}
                                />
                                Create Sub Folder
                              </button>
                              <button
                                type="button"
                                className="cm-menu-item"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setFolderMenuOpenFor(null);
                                  handleEditFolder(id);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  style={{ marginRight: 10 }}
                                />
                                Edit
                              </button>
                              <button
                                type="button"
                                className="cm-menu-item danger"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setFolderMenuOpenFor(null);
                                  handleDeleteFolder(id);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faTrashAlt}
                                  style={{ marginRight: 10 }}
                                />
                                Delete
                              </button>
                            </div>
                          )}
                        </span>
                        {hasChildren && (
                          <button
                            type="button"
                            className="cm-folder-expand"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleExpanded(id);
                            }}
                          >
                            {isExpanded ? "−" : "+"}
                          </button>
                        )}
                      </div>
                      {hasChildren && isExpanded && (
                        <div className="cm-folder-children">
                          {children.map((c) => renderNode(c, level + 1))}
                        </div>
                      )}
                    </div>
                  );
                };
                return rootFolders.map((f) => renderNode(f, 0));
              })()
            )}
          </div>

          <button
            type="button"
            className="cm-new-folder"
            onClick={() => {
              setCreateParentFolderId(null);
              setIsCreateFolderModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faFolderPlus} /> New Folder
          </button>
        </aside>

        <main className="cm-main">
          <div className="cm-table-card cm-empty-main">
            <div className="cm-empty-main-title">Choose a folder</div>
            <div className="cm-empty-main-text">
              Pick a folder from the left panel to view documents.
            </div>
          </div>
        </main>
      </div>

      {/* Create Folder Modal */}
      <CreateComplianceFolderModal
        isOpen={isCreateFolderModalOpen}
        onClose={() => {
          setIsCreateFolderModalOpen(false);
          setCreateParentFolderId(null);
        }}
        onCreate={handleCreateFolderAPI}
      />

      {/* Edit Folder Modal */}
      <Modal
        isOpen={isEditFolderModalOpen}
        onClose={handleCloseEditFolderModal}
        title="Edit Compliance Folder"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleUpdateFolder();
          }}
        >
          <div className="form-group">
            <label>Folder Name *</label>
            <input
              type="text"
              value={editFolderName}
              onChange={(e) => setEditFolderName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              rows="3"
              value={editFolderDescription}
              onChange={(e) => setEditFolderDescription(e.target.value)}
            ></textarea>
          </div>
          <div className="form-group">
            <label>
              <FontAwesomeIcon
                icon={faPalette}
                style={{ marginRight: "8px" }}
              />
              Folder Color
            </label>
            <div
              className="color-picker-grid"
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "10px",
                flexWrap: "wrap",
              }}
            >
              {folderColors.map((color) => (
                <div
                  key={color}
                  onClick={() => setEditFolderColor(color)}
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "8px",
                    backgroundColor: color,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border:
                      editFolderColor === color
                        ? "3px solid #333"
                        : "2px solid transparent",
                    transition: "all 0.2s ease",
                  }}
                >
                  {editFolderColor === color && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ color: "white", fontSize: "14px" }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="button cancel-button"
              onClick={handleCloseEditFolderModal}
            >
              CANCEL
            </button>
            <button type="submit" className="button update-button">
              UPDATE FOLDER
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteConfirmModalOpen}
        onClose={handleCloseDeleteConfirmModal}
        title="Confirm Deletion"
      >
        <div style={{ marginBottom: "20px" }}>
          <p
            style={{
              fontWeight: "bold",
              color: "#f44336",
              marginBottom: "10px",
            }}
          >
            ⚠️ Warning: This action cannot be undone!
          </p>
          <p>
            Are you sure you want to delete folder "
            <strong>{folderToDelete?.name}</strong>"?
          </p>
          <p style={{ fontSize: "14px", color: "#666", marginTop: "10px" }}>
            All documents inside this folder will also be permanently deleted.
          </p>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="button cancel-button"
            onClick={handleCloseDeleteConfirmModal}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="button delete-button"
            onClick={confirmDeleteFolder}
          >
            DELETE FOLDER
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ComplianceManagement;
